//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { Radio } from 'antd';
import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useI18nContext } from 'components/I18nProvider';
import InlineMessage, { Types as InlineMessageTypes } from 'components/InlineMessage';
import InputWithLabel from 'components/inputs/InputWithLabel';
import { HStack, Stack } from 'components/layout';
import PythonCodeEditor from 'components/PythonCodeEditor/PythonCodeEditor';
import { SelectWinnerTestResult, TestInputs } from 'components/PythonCodeEditor/PythonCodeTestPanel';
import { SkullInput, SkullItem, useSkullConfigContext } from 'components/skull';
import TabPanelSpin from 'components/TabPanelSpin';
import AppConstants from 'utils/AppConstants';
import { getDefaultValue } from 'utils/InputUtil';
import { base64DecodeUtf8, base64EncodeUtf8 } from 'utils/StringUtil';
import { isNotNullOrUndefined } from 'utils/TypeUtils';
import './ConfigPage.less';

// This wizard (Merge Studio, the CORE_ENTITY node's config drawer) is the only place these two
// datatypes are rendered from - every field here should use the v2 Filter, not just specific
// named fields like "Skip When"/"Find Duplicates", since exact backend field names in this
// dynamic, schema-driven system aren't a reliable thing to hardcode against (see InputContainer's
// PREDICATE case, which opts into v2 Filter only when a filterVersion: 'v2' prop is present).
//
// alwaysShowDelete + cascadeDeleteAtRoot (both existing opt-in Filter props) mean a lone
// condition's own delete icon is always visible and, when clicked, removes the whole composite
// row it's nested in - see CompositeGroup.tsx, which wires up the actual cascade for any
// sub-field carrying filterVersion: 'v2'. A rule (row) here is exactly one condition group, so
// its last condition going away means the rule itself is gone.
const withV2Predicates = (configuration?: SkullItem[]) =>
  configuration?.map((subField) =>
    subField.datatype === AppConstants.INPUT_TYPE.PREDICATE
      ? { ...subField, filterVersion: 'v2', alwaysShowDelete: true, cascadeDeleteAtRoot: true }
      : subField
  );

// Whether this composite field's own row (CompositeGroup) should get the v2 redesign - the same
// composites that get withV2Predicates applied to their nested predicate sub-field above.
const hasV2Predicate = (input: SkullInput) =>
  input.datatype === AppConstants.INPUT_TYPE.COMPOSITE &&
  input.configuration?.some((subField) => subField.datatype === AppConstants.INPUT_TYPE.PREDICATE);

const SELECT_WINNER_DEFAULT_COMMENT = `# You have access to the following variables - incoming:dict , duplicates:List[dict]
# The code must return a single record id string

# Example: Select the record whose address city is "San Francisco"
for record in [incoming] + duplicates:
    if record.get("address", {}).get("city") == "San Francisco":
        return record["id"]
return incoming["id"]
`;
const SELECT_WINNER_CONTEXT_WORDS = ['incoming', 'duplicates'];

const MERGE_POLICY_DEFAULT_COMMENT = `# You have access to the following variables - winner:dict , losers:List[dict]
# The code must return a single record as a dict

# Example: Copy the latest loser's annual_revenue to the winner,
# and merge address so every field has a value (prefer the most recently modified loser)
sorted_losers = sorted(losers, key=lambda r: r.get("lastModified", ""), reverse=True)

if sorted_losers:
    winner["annual_revenue"] = sorted_losers[0]["annual_revenue"]

address = winner.get("address", {})
for field in ["street", "city", "state", "zip", "country"]:
    if not address.get(field):
        for loser in sorted_losers:
            val = loser.get("address", {}).get(field)
            if val:
                address[field] = val
                break
winner["address"] = address
return winner
`;
const MERGE_POLICY_CONTEXT_WORDS = ['winner', 'losers'];

const StableInputContainer = ({
  input,
  onSelectWinnerTestResult,
  selectWinnerTestResult,
  editorTab,
  onEditorTabChange,
  siblingInputs,
}: {
  input: SkullInput;
  onSelectWinnerTestResult?: (result: SelectWinnerTestResult) => void;
  selectWinnerTestResult?: SelectWinnerTestResult;
  editorTab?: string;
  onEditorTabChange?: (tab: string) => void;
  siblingInputs?: SkullInput[];
}) => {
  const { fetchPicklistValues, graphNodeValue, onChange, picklistValues, values } = useSkullConfigContext();
  const { tc } = useI18nContext();

  const name = input.name;

  const isSelectWinner = input.datatype === 'composite' && input.name === 'selectWinnerValue';
  const isFieldLevelOverrides = input.datatype === 'composite' && input.name === 'fieldMergePolicies';
  const hasCustomCodeToggle = isSelectWinner || isFieldLevelOverrides;

  // Both of these composites carry a predicate sub-field (winnerSelectionPredicate /
  // fieldMergePredicate), so the v2 opt-in below would otherwise hide their row delete - but
  // neither surfaces the condition-level delete that's supposed to replace it, which left their
  // saved rows with no way to be removed. They stay on the pre-v2 behaviour: the row delete
  // renders unless the field itself asks otherwise. Only Find Duplicates uses the new pattern.
  const keepsRowDelete = isSelectWinner || isFieldLevelOverrides;

  const customCodeName = isSelectWinner ? 'selectWinnerValueCustomCode' : 'mergePoliciesCustomCode';
  const savedCustomCodeValue = (values?.[customCodeName]?.value ??
    (graphNodeValue as any)?.configuration?.[customCodeName]) as
    | { useCustomCode?: boolean; customCode?: string; testInputs?: TestInputs }
    | undefined;

  const savedTestInputs = savedCustomCodeValue?.testInputs;

  const [useCustomCode, setUseCustomCode] = useState(savedCustomCodeValue?.useCustomCode ?? false);
  const [customCode, setCustomCode] = useState(() => {
    const encoded = savedCustomCodeValue?.customCode;
    if (!encoded) return '';
    try {
      return base64DecodeUtf8(encoded);
    } catch {
      return encoded;
    }
  });
  const customCodeRef = useRef(customCode);
  const testInputsRef = useRef<TestInputs | undefined>(savedTestInputs);

  const saveCustomCodeValue = useCallback(
    (overrides: { useCustomCode?: boolean; customCode?: string; testInputs?: TestInputs }) => {
      onChange({
        value: {
          useCustomCode: overrides.useCustomCode ?? true,
          customCode: overrides.customCode ?? base64EncodeUtf8(customCodeRef.current),
          testInputs: overrides.testInputs ?? testInputsRef.current,
        },
        name: customCodeName,
      });
    },
    [customCodeName, onChange]
  );

  const onChangeHandler = useCallback(
    (value: any, ...rest: any) => {
      // Predicates... :(
      if (rest?.[1]) {
        onChange.call(undefined, { value: rest[1], name });
      } else if (typeof value === 'string') {
        // Select
        onChange.call(undefined, { value, name });
      } else if (isNotNullOrUndefined(value?.currentTarget?.value)) {
        // html input
        onChange.call(undefined, { name, value: value.currentTarget.value });
      } else if (typeof value?.target?.checked === 'boolean') {
        onChange({
          name,
          value: value?.target?.checked,
        });
      } else if (value?.target?.value) {
        onChange.call(undefined, { name, value: value.target.value });
      } else {
        // Move the name and value transformation in this level instead of
        // doing it in the component level
        // Need to revisit this so it will not break dedup merge.
        onChange({
          name,
          value,
          ...value,
        });
      }
    },
    [name, onChange]
  );

  const handleCustomCodeChange = useCallback(
    (code: string) => {
      setCustomCode(code);
      customCodeRef.current = code;
      saveCustomCodeValue({ customCode: base64EncodeUtf8(code) });
    },
    [saveCustomCodeValue]
  );

  const handleTestInputsChange = useCallback(
    (inputs: TestInputs) => {
      testInputsRef.current = inputs;
      saveCustomCodeValue({ testInputs: inputs });
    },
    [saveCustomCodeValue]
  );

  const handleModeChange = useCallback(
    (e: any) => {
      const isCustom = e.target.value === 'customCode';
      setUseCustomCode(isCustom);
      saveCustomCodeValue({
        useCustomCode: isCustom,
      });
    },
    [saveCustomCodeValue]
  );

  const defaultLabel = tc('use_conditions');

  const renderInput = () => (
    <InputWithLabel
      key={`config-page-input-${input.name}`}
      onChange={onChangeHandler}
      fetchPicklistValues={fetchPicklistValues}
      picklistValues={picklistValues}
      tooltip={input.helpSummary}
      values={input.values}
      defaultValue={getDefaultValue(input?.defaultValue)}
      {...input}
      // Route this step's own top-level predicate field (e.g. "Skip When") and any nested
      // predicate sub-field of a composite field (e.g. "Find Duplicates"/"Select Winner") to v2
      // Filter - see withV2Predicates above and InputContainer's PREDICATE case.
      filterVersion={input.datatype === AppConstants.INPUT_TYPE.PREDICATE ? 'v2' : undefined}
      configuration={
        input.datatype === AppConstants.INPUT_TYPE.COMPOSITE
          ? withV2Predicates(input.configuration)
          : input.configuration
      }
      // Redesigned row styling (white background, #757575 icons, alignment fix) + hide the
      // row's own delete button, since FilterCondition's own delete (always shown via
      // alwaysShowDelete above) covers removing the whole row instead - as it does in Find
      // Duplicates. The keepsRowDelete steps above are excluded and keep their pre-v2 row delete
      // (restyled for these rows in CompositeGroup.less).
      // Note: InputWithLabel's own `className` prop only ever reaches its outer wrapper div, not
      // the Composite it renders (see InputWithLabel.tsx) - inputClassName is what actually
      // reaches Composite/CompositeGroup, which is what this scoping depends on.
      inputClassName={hasV2Predicate(input) ? 'synri-composite-v2' : undefined}
      hideDelete={hasV2Predicate(input) && !keepsRowDelete ? true : input.hideDelete}
    />
  );

  if (!hasCustomCodeToggle) {
    return renderInput();
  }

  const renderSiblingInput = (siblingInput: SkullInput) => (
    <StableInputContainer key={siblingInput.id} input={siblingInput} />
  );

  return (
    <Stack spacing="md">
      <Radio.Group value={useCustomCode ? 'customCode' : 'conditions'} onChange={handleModeChange}>
        <Radio.Button value="conditions">{defaultLabel}</Radio.Button>
        <Radio.Button value="customCode">{tc('use_custom_code')}</Radio.Button>
      </Radio.Group>
      {useCustomCode ? (
        <PythonCodeEditor
          value={customCode}
          onChange={handleCustomCodeChange}
          defaultComment={isSelectWinner ? SELECT_WINNER_DEFAULT_COMMENT : MERGE_POLICY_DEFAULT_COMMENT}
          contextWords={isSelectWinner ? SELECT_WINNER_CONTEXT_WORDS : MERGE_POLICY_CONTEXT_WORDS}
          isSelectWinner={isSelectWinner}
          onSelectWinnerTestResult={isSelectWinner ? onSelectWinnerTestResult : undefined}
          selectWinnerTestResult={!isSelectWinner ? selectWinnerTestResult : undefined}
          activeTab={editorTab}
          onActiveTabChange={onEditorTabChange}
          savedTestInputs={savedTestInputs}
          onTestInputsChange={handleTestInputsChange}
        />
      ) : (
        <Stack spacing="md">
          {siblingInputs?.map(renderSiblingInput)}
          {renderInput()}
        </Stack>
      )}
    </Stack>
  );
};

export interface ConfigPageProps {
  className?: string;
}

// Composite inputs that have a custom code toggle
const CUSTOM_CODE_COMPOSITES = new Set(['selectWinnerValue', 'fieldMergePolicies']);

const ConfigPage = ({ className }: ConfigPageProps) => {
  const { currentStep: currentStepIndex, errorMessage, inputs, loadingNextStep, steps } = useSkullConfigContext();
  const [selectWinnerTestResult, setSelectWinnerTestResult] = useState<SelectWinnerTestResult>();
  const [editorTabs, setEditorTabs] = useState<Record<string, string>>({});

  const { tc } = useI18nContext();

  const currentStep = steps?.[currentStepIndex] || {};

  const { type, className: containerClassName, ...layoutProps } = currentStep.layout || {};
  const Component = type === 'hstack' ? HStack : Stack;

  // Find the composite input on this step that has the custom code toggle
  const compositeInput = inputs?.find((i) => CUSTOM_CODE_COMPOSITES.has(i.name));

  // All other inputs on the step are siblings — they belong under "Use Conditions"
  const ownedInputNames = new Set<string>();
  const siblingInputsMap: Record<string, SkullInput[]> = {};
  if (compositeInput && inputs) {
    const siblings = inputs.filter((i) => i.name !== compositeInput.name);
    siblings.forEach((s) => ownedInputNames.add(s.name));
    siblingInputsMap[compositeInput.name] = siblings;
  }

  return (
    <TabPanelSpin spinning={loadingNextStep} tip={tc('loading_step')}>
      <Component className={cx('synri-config-page-container', className, containerClassName)} {...layoutProps}>
        {!!errorMessage && (
          <InlineMessage type={InlineMessageTypes.ERROR} title={errorMessage}>
            {errorMessage}
          </InlineMessage>
        )}
        {inputs
          ?.filter((input) => !ownedInputNames.has(input.name))
          .map((input) => (
            <StableInputContainer
              key={input.id}
              input={input}
              onSelectWinnerTestResult={setSelectWinnerTestResult}
              selectWinnerTestResult={selectWinnerTestResult}
              editorTab={editorTabs[input.name] ?? 'code'}
              onEditorTabChange={(tab) => setEditorTabs((prev) => ({ ...prev, [input.name]: tab }))}
              siblingInputs={siblingInputsMap[input.name]}
            />
          ))}
      </Component>
    </TabPanelSpin>
  );
};

export default ConfigPage;
